import { isArray } from '@ember/array';
import { assert } from '@ember/debug';
import { concat, fn } from '@ember/helper';
import { action } from '@ember/object';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { set } from 'ember-set-helper';
import or from 'ember-truth-helpers/helpers/or';
import isEqual from 'lodash/isEqual';
import BvChoiceBlocksBlockComponent from 'volta/components/bv-choice-blocks/block';
import BvLabelled from 'volta/components/bv-labelled';
import BvText from 'volta/components/bv-text';
import classNames from 'volta/helpers/class-names';
import isEq from 'volta/helpers/is-eq';
import isPresent from 'volta/helpers/is-present';
import orElse from 'volta/helpers/or-else';
import useState from 'volta/helpers/use-state';
import cssVars from 'volta/modifiers/css-vars';

import type { ComponentLike, WithBoundArgs } from '@glint/template';
import type { BvChoiceBlocksBlockSignature } from 'volta/components/bv-choice-blocks/block';
import type { BvIconSignature, IBvIconArgs } from 'volta/components/bv-icon';
import type { BvLabelledSignature } from 'volta/components/bv-labelled';
import type { TSize } from 'volta/utils/style-utils';

export interface IChoiceItem {
  icon?: string;
  iconColor?: IBvIconArgs['color'];
  disabled?: boolean;
  title?: string;
  description?: string;
  hoveredDescription?: string;
  allowDeselection?: boolean;
  value: any;
}

export interface IChoiceSection {
  title?: string;
  description?: string;
  items: IChoiceItem[];
}

export type BvChoiceBlocksArgs = Pick<
  BvLabelledSignature['Args'],
  'label' | 'error' | 'warning' | 'helpText'
> & {
  checked?: unknown;
  auto?: boolean;
  onChange?: (value: any, e: Event) => void;
  items?: IChoiceItem[];
  sections?: IChoiceSection[];
  allowDeselection?: boolean;
  useDescriptionAsHelp?: boolean;
  maxColumns?: string;
  vertical?: boolean;
  disabled?: boolean;
  size?: Extract<TSize, 'xs' | 'sm' | 'md' | 'lg'>;
  containerClass?: string;
  iconColor?: BvIconSignature['Args']['color'];
  inlineIcon?: boolean;
};

interface BvChoiceBlocksSignature {
  Args: BvChoiceBlocksArgs;
  Blocks: {
    default: [
      WithBoundArgs<
        ComponentLike<BvChoiceBlocksBlockSignature>,
        | 'item'
        | 'size'
        | 'iconColor'
        | 'disabled'
        | 'description'
        | 'checked'
        | 'inlineIcon'
        | 'onChange'
        | 'onHover'
        | 'onMouseEnter'
        | 'onMouseLeave'
      >,
      IChoiceItem
    ];
  };
  Element: HTMLFieldSetElement;
}

export default class BvChoiceBlocksComponent extends Component<BvChoiceBlocksSignature> {
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked checked = this.args.checked;

  @tracked hoveredItem?: IChoiceItem;

  get finalSections() {
    const { items = [], sections = [] } = this.args;
    const validItems = items.filter(Boolean);
    const validSections = sections.filter((s) => s.items.length > 0);
    const finalSections: IChoiceSection[] = [];

    if (validItems.length > 0) {
      finalSections.push({ items: validItems });
    }

    assert(
      `bevolta::bv-choice-blocks - sections must be an array, you passed ${sections}`,
      isArray(sections)
    );

    if (validSections.length > 0) {
      finalSections.push(...validSections);
    }

    return finalSections;
  }

  get hasMultipleSections() {
    return this.finalSections.length > 1;
  }

  @action
  onChange(value: any, e: MouseEvent) {
    if (this.args.allowDeselection) {
      const deepEqual = isEqual(this.checked, value);
      if (deepEqual) {
        this.changeCheck(null, e);
      } else {
        this.changeCheck(value, e);
      }
    } else {
      this.changeCheck(value, e);
    }
  }

  @action
  handleCheckedChange(_el: HTMLElement, [value]: [any]) {
    if (this.args.auto && !isEqual(this.checked, value)) {
      this.checked = value;
    }
  }

  changeCheck(value: unknown, e: MouseEvent) {
    this.checked = value;
    this.args.onChange?.(value, e);
  }

  showDescriptionOnMouseLeave = (): boolean => {
    const { auto, checked } = this.args;
    const items = this.finalSections.flatMap((s) => s.items);
    const isChecked = auto ? this.checked : checked;
    if (isChecked) {
      return !!items.find((item: IChoiceItem) => item.value === isChecked)?.hoveredDescription;
    }
    return false;
  };

  <template>
    {{#let (useState false) as |showDescription|}}
      {{! template-lint-disable no-at-ember-render-modifiers }}
      <BvLabelled
        @label={{@label}}
        @error={{@error}}
        @warning={{@warning}}
        @helpText={{if
          @useDescriptionAsHelp
          this.hoveredItem.description
          (unless showDescription.state @helpText)
        }}
        {{didUpdate this.handleCheckedChange @checked}}
      >
        {{#let
          (classNames
            'bv-choice-blocks'
            (if @maxColumns 'bv-choice-blocks--justified')
            (if @vertical 'bv-choice-blocks--vertical')
            (if @disabled 'bv-choice-blocks--disabled')
            (concat 'bv-choice-blocks--' (orElse @size 'lg'))
            (if this.hasMultipleSections 'bv-choice-blocks--has-multiple-sections')
            @containerClass
          )
          as |classNames|
        }}
          <fieldset role='radiogroup' class={{classNames}} ...attributes>
            {{#each this.finalSections as |section|}}
              <div class='bv-choice-blocks-section'>
                {{#if (or section.title section.description)}}
                  <div class='bv-choice-blocks-section__header'>
                    {{#if section.title}}
                      <BvText
                        class='bv-choice-blocks-section__title u-color-text-subdued'
                        @text={{section.title}}
                        @fontWeight='semibold'
                      />
                    {{/if}}
                    {{#if section.description}}
                      <BvText
                        class='bv-choice-blocks-section__description u-mt-1'
                        @text={{section.description}}
                        @color='subdued'
                      />
                    {{/if}}
                  </div>
                {{/if}}
                <div
                  class='bv-choice-blocks-section__items'
                  {{cssVars choice-blocks-columns=@maxColumns}}
                >
                  {{#each section.items key='value' as |item|}}
                    {{#let
                      (or item.disabled @disabled)
                      (isEq item.value (if @auto this.checked @checked))
                      as |disabled checked|
                    }}
                      {{#let
                        (component
                          BvChoiceBlocksBlockComponent
                          item=item
                          size=@size
                          iconColor=(orElse item.iconColor @iconColor)
                          disabled=disabled
                          description=(unless @useDescriptionAsHelp item.description)
                          checked=checked
                          inlineIcon=@inlineIcon
                          onChange=(fn this.onChange item.value)
                          onHover=(set this 'hoveredItem' item)
                          onMouseEnter=(fn
                            showDescription.setState (isPresent item.hoveredDescription)
                          )
                          onMouseLeave=(fn
                            showDescription.setState (this.showDescriptionOnMouseLeave)
                          )
                        )
                        as |Block|
                      }}
                        {{#if (has-block)}}
                          {{yield Block item}}
                        {{else}}
                          <Block />
                        {{/if}}
                      {{/let}}
                    {{/let}}
                  {{/each}}
                </div>
              </div>
            {{/each}}
          </fieldset>
        {{/let}}
      </BvLabelled>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvChoiceBlocks: typeof BvChoiceBlocksComponent;
    'bv-choice-blocks': typeof BvChoiceBlocksComponent;
  }
}
